import React, { forwardRef, useMemo } from "react";
import {
  List,
  type RowComponentProps,
  type ListImperativeAPI,
} from "react-window";
import { Box, ScrollArea, useMantineTheme } from "@mantine/core";

export interface VirtualizedListProps<T = any> {
  /** Array of items to render */
  items: T[];
  /** Height of each item in pixels, or function for variable height */
  itemHeight: number | ((index: number, props: { items: T[] }) => number);
  /** Total height of the list container */
  height?: number;
  /** Width of the list container */
  width?: number | string;
  /** Function to render each item */
  renderItem: (props: VirtualizedListItemProps<T>) => React.ReactNode;
  /** Number of items to render outside of the visible area */
  overscanCount?: number;
  /** Custom className for the container */
  className?: string;
  /** Custom styles for the container */
  style?: React.CSSProperties;
  /** Whether to use Mantine's ScrollArea for styling */
  useMantineScrollArea?: boolean;
  /** Loading state */
  loading?: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen. `loading && items.length === 0` is TRUE on
   * the first render, before the request has started, so the empty state painted first.
   * ▶ npm run check-list-states
   */
  isLoaded: boolean;
  /** Empty state component */
  emptyComponent?: React.ReactNode;
  /** Loading component */
  loadingComponent?: React.ReactNode;
  /** Called when visible rows change */
  onRowsRendered?: (visibleRows: {
    startIndex: number;
    stopIndex: number;
  }) => void;
  /** Called when scrolled to bottom */
  onScrollToBottom?: () => void;
  /** Threshold for triggering onScrollToBottom (pixels from bottom) */
  scrollToBottomThreshold?: number;
}

export interface VirtualizedListItemProps<T = any> {
  /** The item data */
  item: T;
  /** Index of the item */
  index: number;
  /** Style object for positioning (from react-window) */
  style: React.CSSProperties;
  /** ARIA attributes for accessibility */
  ariaAttributes: {
    "aria-posinset": number;
    "aria-setsize": number;
    role: "listitem";
  };
}

export const VirtualizedList = <T,>({
  items,
  itemHeight,
  height = 400,
  width = "100%",
  renderItem,
  overscanCount = 5,
  className,
  style,
  useMantineScrollArea = true,
  loading = false,
  isLoaded,
  emptyComponent,
  loadingComponent,
  onRowsRendered,
  onScrollToBottom,
  scrollToBottomThreshold = 100,
}: VirtualizedListProps<T>) => {
  const theme = useMantineTheme();

  // Create the row component that will be passed to react-window
  const RowComponent = useMemo(() => {
    return ({
      index,
      style: rowStyle,
      ariaAttributes,
      items: rowItems,
    }: RowComponentProps<{ items: T[] }>): React.ReactElement => {
      const item = rowItems[index];

      if (!item) {
        return <div style={rowStyle} />;
      }

      const itemProps: VirtualizedListItemProps<T> = {
        item,
        index,
        style: rowStyle,
        ariaAttributes,
      };

      // Wrap in fragment to ensure we always return ReactElement
      return <>{renderItem(itemProps)}</>;
    };
  }, [renderItem]);

  // Handle rows rendered for infinite scrolling
  const handleRowsRendered = useMemo(() => {
    return (
      visibleRows: { startIndex: number; stopIndex: number }
      //allRows: { startIndex: number; stopIndex: number }
    ) => {
      // Call the provided onRowsRendered callback
      onRowsRendered?.(visibleRows);

      // Check if we need to trigger scroll to bottom
      if (onScrollToBottom) {
        const { stopIndex } = visibleRows;
        const remainingItems = items.length - stopIndex - 1;

        if (
          remainingItems <=
          scrollToBottomThreshold /
            (typeof itemHeight === "number" ? itemHeight : 50)
        ) {
          onScrollToBottom();
        }
      }
    };
  }, [
    onRowsRendered,
    onScrollToBottom,
    items.length,
    itemHeight,
    scrollToBottomThreshold,
  ]);

  // Show loading state
  if (loading && loadingComponent) {
    return (
      <Box
        className={className || ""}
        style={{
          height,
          width,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...style,
        }}
      >
        {loadingComponent}
      </Box>
    );
  }

  // Show empty state
  if (isLoaded && items.length === 0) {
    if (emptyComponent) {
      return (
        <Box
          className={className || ""}
          style={{
            height,
            width,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...style,
          }}
        >
          {emptyComponent}
        </Box>
      );
    }
    return null;
  }

  const listComponent = (
    <List
      rowComponent={RowComponent}
      rowCount={items.length}
      rowHeight={itemHeight}
      rowProps={{ items }}
      overscanCount={overscanCount}
      onRowsRendered={handleRowsRendered}
      style={{
        height,
        width,
        ...style,
        backgroundColor: "transparent",
      }}
    />
  );

  // Wrap with Mantine ScrollArea for consistent styling
  if (useMantineScrollArea) {
    return (
      <Box className={className || "scroll-area-container"}>
        <ScrollArea
          h={height}
          w={width}
          style={{
            border: `1px solid ${theme.colors.gray[3]}`,
            borderRadius: theme.radius.md,
          }}
        >
          {listComponent}
        </ScrollArea>
      </Box>
    );
  }

  // Return raw list without ScrollArea wrapper
  return (
    <Box
      className={className || ""}
      style={{
        height,
        width,
        border: `1px solid ${theme.colors.gray[3]}`,
        borderRadius: theme.radius.md,
        overflow: "hidden",
      }}
    >
      {listComponent}
    </Box>
  );
};

// Forward ref version for more advanced use cases
export const VirtualizedListRef = forwardRef<
  ListImperativeAPI,
  VirtualizedListProps
>((props, ref) => {
  const {
    items,
    itemHeight,
    height = 400,
    width = "100%",
    renderItem,
    overscanCount = 5,
    className,
    style,
    useMantineScrollArea = true,
    loading = false,
    isLoaded,
    emptyComponent,
    loadingComponent,
    onRowsRendered,
    onScrollToBottom,
    scrollToBottomThreshold = 100,
  } = props;

  const theme = useMantineTheme();

  const RowComponent = useMemo(() => {
    return ({
      index,
      style: rowStyle,
      ariaAttributes,
      items: rowItems,
    }: RowComponentProps<{ items: any[] }>): React.ReactElement => {
      const item = rowItems[index];

      if (!item) {
        return <div style={rowStyle} />;
      }

      const itemProps: VirtualizedListItemProps = {
        item,
        index,
        style: rowStyle,
        ariaAttributes,
      };

      // Wrap in fragment to ensure we always return ReactElement
      return <>{renderItem(itemProps)}</>;
    };
  }, [renderItem]);

  const handleRowsRendered = useMemo(() => {
    return (
      visibleRows: { startIndex: number; stopIndex: number }
      //allRows: { startIndex: number; stopIndex: number }
    ) => {
      onRowsRendered?.(visibleRows);

      if (onScrollToBottom) {
        const { stopIndex } = visibleRows;
        const remainingItems = items.length - stopIndex - 1;

        if (
          remainingItems <=
          scrollToBottomThreshold /
            (typeof itemHeight === "number" ? itemHeight : 50)
        ) {
          onScrollToBottom();
        }
      }
    };
  }, [
    onRowsRendered,
    onScrollToBottom,
    items.length,
    itemHeight,
    scrollToBottomThreshold,
  ]);

  if (loading && loadingComponent) {
    return (
      <Box
        className={className || ""}
        style={{
          height,
          width,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...style,
        }}
      >
        {loadingComponent}
      </Box>
    );
  }

  if (isLoaded && items.length === 0) {
    if (emptyComponent) {
      return (
        <Box
          className={className || ""}
          style={{
            height,
            width,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...style,
          }}
        >
          {emptyComponent}
        </Box>
      );
    }
    return null;
  }

  const listComponent = (
    <List
      listRef={ref}
      rowComponent={RowComponent}
      rowCount={items.length}
      rowHeight={itemHeight}
      rowProps={{ items }}
      overscanCount={overscanCount}
      onRowsRendered={handleRowsRendered}
      style={{
        height,
        width,
        ...style,
        backgroundColor: "transparent",
      }}
    />
  );

  if (useMantineScrollArea) {
    return (
      <Box className={className || ""}>
        <ScrollArea
          h={height}
          w={width}
          style={{
            border: `1px solid ${theme.colors.gray[3]}`,
            borderRadius: theme.radius.md,
          }}
        >
          {listComponent}
        </ScrollArea>
      </Box>
    );
  }

  return (
    <Box
      className={className || "list-container"}
      style={{
        height,
        width,
        border: `1px solid ${theme.colors.gray[3]}`,
        borderRadius: theme.radius.md,
        overflow: "hidden",
      }}
    >
      {listComponent}
    </Box>
  );
});

VirtualizedListRef.displayName = "VirtualizedListRef";
